import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router";
import { styled } from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { FilterButton, HeaderTitle, MainLayout, SectionTitleUnderlined } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { breakpoints, mediaStyles } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { activateModal } from "domains/modal/actions";
import { NoAccessSidebar } from "domains/monitor/noAccess/components/NoAccessSidebar";
import { EmptySearchResult } from "domains/search/components/EmptySearchResult";
import { InteractiveDataResults } from "domains/search/components/interactiveData/InteractiveDataResults";
import { KeyIssueResults } from "domains/search/components/keyIssues/KeyIssueResults";
import { PublicationsResults } from "domains/search/components/publications/PublicationsResults";
import { SearchFilter } from "domains/search/components/SearchFilter/SearchFilter";
import { TopicResults } from "domains/search/components/topics/TopicResults";
import { VideosResults } from "domains/search/components/videos/VideosResult";
import { getEffectiveSearchResultsVisibility, searchSlice } from "domains/search/search.slice";
import { getCanAccessCreate, getCanAccessMonitor } from "entities/permissions/selectors";

const StyledContent = styled.div`
  box-sizing: border-box;
  ${mediaStyles(({ spacing }) => `padding: 0 ${spacing}rem ${spacing}rem ${spacing}rem;`)};

  @media ${breakpoints.extraLarge} {
    padding: 3rem;
  }
`;

const StyledHeaderContainer = styled.div`
  width: 100%;
  margin-bottom: 1rem;

  ${mediaStyles(
    ({ spacing }) => `
      padding: 0 ${spacing}rem;
      margin-top: ${spacing}rem;
    `,
  )};
`;

const StyledSearchContainer = styled.div`
  padding: 1rem 0;
  display: flex;
  width: 100%;
`;

const StyledInput = styled(UIInput)`
  margin-bottom: 1rem;
`;

const StyledResultsContainer = styled.div`
  box-sizing: border-box;
  scroll-padding-top: 2rem;
`;

export const SearchPage = () => {
  const isAuthenticated = useSelector(getIsAuthenticated);
  const track = useTrack();
  const translate = useTranslate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams({ query: "", source: "" });
  const query = searchParams.get("query");

  const currentSearchQuery = useSelector((state) => state.search.currentSearchQuery);
  const canAccessCreate = useSelector(getCanAccessCreate);
  const canAccessMonitor = useSelector(getCanAccessMonitor);

  const { areTopicsVisible, areKeyIssuesVisible, arePublicationsVisible, areVideosVisible, isInteractiveDataVisible } =
    useSelector(getEffectiveSearchResultsVisibility);

  const [searchInput, setSearchInput] = useState(currentSearchQuery);

  const { updateSearchQuery } = searchSlice.actions;

  const debouncedUpdateSearchQuery = useDebouncedCallback((searchQuery: string) => {
    dispatch(updateSearchQuery({ query: searchQuery }));
  }, 1000);

  const isMarketingHidden = canAccessMonitor && canAccessCreate;

  const onFilterClick = () => dispatch(activateModal({ id: "SearchFilter" }));

  useEffect(() => {
    debouncedUpdateSearchQuery(searchInput);
  }, [currentSearchQuery, debouncedUpdateSearchQuery, dispatch, searchInput, updateSearchQuery]);

  useEffect(() => {
    if (query) {
      setSearchInput(query);
      dispatch(updateSearchQuery({ query }));
    }
  }, [dispatch, query, updateSearchQuery]);

  const sidePanel = (
    <>
      <SectionTitleUnderlined>{translate("app.search.title")}</SectionTitleUnderlined>
      <StyledInput
        clear={() => setSearchInput("")}
        isPending={false}
        name="searchQuery"
        onChange={setSearchInput}
        placeholder={translate("app.discover.topic.tabs.knowledge.filter.search_title")}
        value={searchInput}
      />
      <SearchFilter />
      {!isMarketingHidden && (
        <div style={{ marginTop: "1rem" }}>
          <NoAccessSidebar
            trackPage="Search"
            text={translate(isAuthenticated ? "app.marketing.upgrade_message" : "app.user.sign_up_message")}
          />
        </div>
      )}
    </>
  );

  const titleElement = (
    <StyledHeaderContainer>
      <HeaderTitle>{translate("app.search.title")}</HeaderTitle>
      <StyledSearchContainer>
        <UIInput
          clear={() => setSearchInput("")}
          isPending={false}
          name="searchQuery"
          onChange={setSearchInput}
          placeholder={translate("app.discover.topic.tabs.knowledge.filter.search_title")}
          value={searchInput}
        />
        <FilterButton onClick={onFilterClick} />
      </StyledSearchContainer>
    </StyledHeaderContainer>
  );

  useEffect(() => track({ name: "Browse page", page: "Search" }), [track]);

  return (
    <MainLayout headerElement={titleElement} sidePanel={sidePanel}>
      <StyledContent>
        <StyledResultsContainer>
          <EmptySearchResult />
          {areTopicsVisible && <TopicResults />}
          {areKeyIssuesVisible && <KeyIssueResults />}
          {arePublicationsVisible && <PublicationsResults />}
          {areVideosVisible && <VideosResults />}
          {isInteractiveDataVisible && <InteractiveDataResults />}
        </StyledResultsContainer>
      </StyledContent>
    </MainLayout>
  );
};
